import React, { useContext, useEffect, useState } from 'react'

import Dialog from '@react/react-spectrum/Dialog'
import Heading from '@react/react-spectrum/Heading'
import SearchWithin from '@react/react-spectrum/SearchWithin'
import Wait from '@react/react-spectrum/Wait'

import { RootContext } from '../../root/RootContext'
import { ProfileContext } from '../../root/ProfileContext'
import { ScannerContext } from '../../root/ScannerContext'
import { useProfile } from '../../data/hooks'

import Operator from '../Operator/Operator'

import { QrReader } from 'react-qr-reader'

import './Header.scss'

export default function Header() {
  const { dr, project } = useContext(RootContext)
  const profileContext = useContext(ProfileContext)
  const scannerContext = useContext(ScannerContext)
  const [idNamespace, setIdNamespace] = useState('-')
  const [idValue, setIdValue] = useState('')
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [showScannerDialog, setShowScannerDialog] = useState(false)
  const [scanData, setScanData] = useState()
  const [startSearch, setStartSearch] = useState(false)

  // hook to fetch profile with given parameters
  const [{ data, loading, error }, fetchProfile] = useProfile({
    orgId: project?.integrations?.aep?.imsOrg?.id,
    sandboxName: project?.integrations?.aep?.sandbox,
    entityIdNS: idNamespace,
    entityId: idValue?.trim(),
  })

  const setDataLayer = (p) => {
    try {
      const tenantId = '_' + project?.integrations?.aep?.imsOrg?.tenantID
      const identification = p.entity[tenantId]?.identification
      const identityMap = p.entity.identityMap

      const profile = {
        loyaltyId: identification?.core?.loyaltyId ?? identification?.retail?.loyaltyId,
        email: identification?.core?.email || identityMap.email?.[0]?.id,
        ecid: identification?.core?.ecid || identityMap.ecid?.[0]?.id,
        phoneNumber: identification?.core?.phoneNumber || identityMap.ecid?.[0]?.phoneNumber,
        accountId: identification?.fsi?.accountId || identityMap.ecid?.[0]?.accountId,
        crmId: identification?.core?.crmId || identityMap.ecid?.[0]?.crmId,
      }

      window.dataLayer.profile = profile
    } catch (e) {
      console.error(111, e)
      setShowErrorDialog(true)
    }
  }

  useEffect(() => {
    if (error && !showErrorDialog) {
      console.error(error)
      setShowErrorDialog(true)
    }
  }, [error])

  const hide = () => {
    setShowErrorDialog(false)
  }

  const hideScan = () => {
    setShowScannerDialog(false)
    setScanData()
  }

  useEffect(() => {
    if (data) {
      const profile = data.profile

      // if fetched profile is different than the current profile, let all the other components know
      if (profile?.entityId !== profileContext.state.currentProfile?.entityId) {
        profileContext.dispatch({
          type: 'profile/SET_CURRENT_PROFILE',
          data,
        })
        setDataLayer(profile)
      }
    }
  }, [data])

  useEffect(() => {
    if (idNamespace !== 'qrcode' && startSearch) {
      fetchProfile()
      setStartSearch(false)
      updateUrl(idNamespace, idValue)
    }
  }, [idNamespace, startSearch])

  //sets the ns/identity values from the QR code to perform profile lookup
  const handleScan = (data) => {
    if (data) {
      try {
        const { entityIdNs, entityId, ...product } = JSON.parse(data)
        setIdNamespace(entityIdNs)
        setIdValue(entityId)
        setScanData(product)
        setShowScannerDialog(false)
        scannerContext.dispatch({
          type: 'scanner/SET_CURRENT_PRODUCT',
          product,
        })
        setStartSearch(true)
      } catch (e) {
        setScanData({ error: 'Invalid QR Code' })
      }
    }
  }

  //called when ns/identity are passed directly from the url (see App.js)
  useEffect(() => {
    if (dr.entityId && dr.entityIdNS) {
      setIdNamespace(dr.entityIdNS)
      setIdValue(dr.entityId)
      setStartSearch(true)
    } else setIdNamespace('email')
  }, [dr])

  return (
    <div>
      <header
        className='header'
        style={{
          backgroundColor: project.branding.mainNavBackgroundColor,
        }}
      >
        <div className='logoTitle'>
          {project.meta.thumbnail ? <img src={project.branding.logo} className='logo' /> : null}
        </div>
        <span className='header-content'>
          <SearchWithin
            scopeOptions={[
              { label: 'CRM ID', value: 'crmId' },
              { label: 'Email', value: 'email' },
              { label: 'Loyalty ID', value: 'loyaltyId' },
              { label: 'Phone number', value: 'phone' },
              { label: 'Mobile number', value: 'mobileNb' },
              { label: 'ECID', value: 'ecid' },
              { label: 'QR code', value: 'qrcode' },
            ]}
            placeholder='Load profile'
            className='profile-search-input'
            defaultScope='email'
            value={idValue}
            onScopeChange={(e) => {
              setIdNamespace(e)
              if (e === 'qrcode') setShowScannerDialog(true)
            }}
            label='Search'
            defautSelectedField='email'
            onValueChange={(e) => setIdValue(e)}
            onSubmit={(e) => {
              if (e !== 'qrcode') {
                setStartSearch(true)
                //remove product from scanner context if any
                scannerContext.dispatch({
                  type: 'scanner/SET_CURRENT_PRODUCT',
                })
              }
            }}
            disabled={loading}
          />

          {loading && <Wait size='S' style={{ marginLeft: 10 }} />}
        </span>
        <span className='current-context'>
          <Operator />
        </span>
        {showErrorDialog && (
          <>
            <div className='spectrum-Underlay is-open'></div>
            <ErrorDialog error={error} onClose={hide} />
          </>
        )}

        {showScannerDialog && (
          <>
            <div className='spectrum-Underlay is-open'></div>
            <ScannerDialog handleScan={handleScan} onClose={hideScan} scanData={scanData} />
          </>
        )}
      </header>
    </div>
  )
}

export const ErrorDialog = ({ error, onClose }) => {
  return (
    <Dialog variant='error' title='Not Found' confirmLabel='Discard' size='L' onConfirm={onClose}>
      {error?.response?.data.message || 'Profile not found'}
    </Dialog>
  )
}

export const ScannerDialog = ({ handleScan, onClose, scanData }) => {
  const handleError = (err) => {
    console.error(err)
  }

  return (
    <Dialog
      variant='information'
      mode='alert'
      title='Scan QR Code'
      confirmLabel='Discard'
      role='dialog'
      onConfirm={onClose}
    >
      <div style={{ textAlign: 'center' }}>
        <QrReader delay={300} onError={handleError} onResult={handleScan} style={{ width: '100%' }} />
        {scanData && scanData.error && <h3>{scanData.error}</h3>}
      </div>
    </Dialog>
  )
}

function updateUrl(ns, val) {
  const pathname = window.location.pathname.split('/').slice(0, 3).concat(ns, val).join('/')
  window.history.replaceState(window.history.data, 'New Page Title', pathname)
}
